import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Card,
  CardMedia,
  CardContent,
  Alert,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { toast } from "react-toastify";

// Redux
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import { submitReviewRequestThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";

// Upload related imports
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";

// Signed URL imports for viewing
import {
  getFileSignedURLMorthThunk as getMorthAccessSignedURLThunk,
  getFileSignedURLThunk as getFileSignedURLAccessThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import {
  getFileSignedURLMorthThunk as getMorthRowSignedURLThunk,
  getFileSignedURLThunk as getFileSignedURLRowThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { getSignedPreviewUrl } from "../../utils/getViewSignedUrlHandler";

// ============ FilePreviewModal ============
const FilePreviewModal = ({
  open,
  onClose,
  file,
  fileType,
  previewDocUrl,
}: {
  open: boolean;
  onClose: () => void;
  file: any;
  fileType: "image" | "video" | "pdf" | "other";
  previewDocUrl: any;
}) => {
  const getPreviewContent = () => {
    switch (fileType) {
      case "image":
        return (
          <CardMedia
            component="img"
            image={previewDocUrl}
            alt={file.fileName}
            sx={{
              maxHeight: { xs: "400px", sm: "500px", md: "700px" },
              width: "100%",
              objectFit: "contain",
            }}
          />
        );
      case "video":
        return (
          <Box display="flex" justifyContent="center">
            <CardMedia
              component="video"
              src={previewDocUrl}
              controls
              autoPlay
              sx={{
                maxHeight: { xs: "300px", sm: "500px", md: "700px" },
                width: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </Box>
        );
      case "pdf":
        return (
          <Box
            sx={{
              width: "100%",
              height: { xs: "400px", sm: "500px", md: "600px" },
              overflow: "auto",
            }}
          >
            <iframe
              src={previewDocUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title={file.fileName}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography>Preview not available for this file type</Typography>
          </Box>
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: "70%" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ p: 3, width: "100%", overflowX: "hidden" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">{file.fileName}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {getPreviewContent()}
      </Box>
    </Drawer>
  );
};

// ============ Types ============
interface DocumentData {
  fileName: string;
  objectKey: string;
  originalFileName?: string;
  fileType: "pdf" | "image" | "video" | "other";
  previewUrl?: string;
  uploadProgress?: number;
  isUploading?: boolean;
}

interface RequestReviewDrawerProps {
  open: boolean;
  onClose: () => void;
  applicationId: string | number;
  isROW?: boolean;
  onSuccess?: () => void;
}

// ============ Main Component ============
export const RequestReviewDrawer: React.FC<RequestReviewDrawerProps> = ({
  open,
  onClose,
  applicationId,
  isROW = false,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [reason, setReason] = useState("");
  // Single file instead of array
  const [uploadedDocument, setUploadedDocument] = useState<DocumentData | null>(
    null,
  );
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocUrl, setPreviewDocUrl] = useState<string>();
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      if (uploadedDocument?.previewUrl) {
        URL.revokeObjectURL(uploadedDocument.previewUrl);
      }
      setReason("");
      setUploadedDocument(null);
      setError(false);
      setIsSubmitting(false);
      setPreviewOpen(false);
      setPreviewDocUrl(undefined);
    }
  }, [open]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (uploadedDocument?.previewUrl) {
        URL.revokeObjectURL(uploadedDocument.previewUrl);
      }
      if (previewDocUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewDocUrl);
      }
    };
  }, []);

  // Cleanup preview URL when it changes
  useEffect(() => {
    return () => {
      if (previewDocUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewDocUrl);
      }
    };
  }, [previewDocUrl]);

  const getObjectKey = (path?: string) => {
    if (!path) return undefined;
    return path.replace(/^(noc-document|morth-documents)\/(row|access)\//, "");
  };

  // ============ FILE UPLOAD HANDLER (SINGLE FILE) ============
  const handleFileUpload = async (file: File): Promise<boolean> => {
    // Validate file size (20 MB max)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return false;
    }

    // If there's already a file, clean up its preview URL first
    if (uploadedDocument?.previewUrl) {
      URL.revokeObjectURL(uploadedDocument.previewUrl);
    }

    const tempObjectKey = `temp-review-request-${Date.now()}-${file.name}`;
    const previewUrl = URL.createObjectURL(file);

    // Determine file type
    const fileType: DocumentData["fileType"] = file.type.startsWith("image/")
      ? "image"
      : file.type === "application/pdf"
        ? "pdf"
        : file.type.startsWith("video/")
          ? "video"
          : "other";

    const tempDocument: DocumentData = {
      fileName: file.name,
      objectKey: tempObjectKey,
      fileType,
      previewUrl,
      isUploading: true,
      uploadProgress: 0,
    };

    // Replace any existing document with the new one
    setUploadedDocument(tempDocument);

    try {
      const signedURLResponse = await dispatch(
        genericUploadThunk({
          file,
          moduleType: isROW ? "row" : "access",
        }),
      );

      if (!signedURLResponse?.payload?.success) {
        const details =
          signedURLResponse?.payload?.details ||
          signedURLResponse?.payload?.response?.data?.details;
        toast.error(
          Array.isArray(details)
            ? details[0]
            : typeof details === "string"
              ? details
              : "Something Went Wrong!",
        );
        setUploadedDocument(null);
        URL.revokeObjectURL(previewUrl);
        return false;
      }

      const { data } = signedURLResponse.payload;

      // Update progress
      setUploadedDocument((prev) =>
        prev && prev.objectKey === tempObjectKey
          ? { ...prev, uploadProgress: 50 }
          : prev,
      );

      // Upload to S3 (skip in production if backend handles it)
      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) throw new Error("S3 upload failed");
      }

      // Create final document object
      const finalDocument: DocumentData = {
        fileName: data.originalFileName || file.name,
        objectKey: data.objectKey,
        originalFileName: data.originalFileName || file.name,
        fileType,
        previewUrl: previewUrl,
      };

      setUploadedDocument((prev) =>
        prev && prev.objectKey === tempObjectKey ? finalDocument : prev,
      );

      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadedDocument(null);
      URL.revokeObjectURL(previewUrl);
      toast.error(`Failed to upload ${file.name}`);
      return false;
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Only take the first file (single file upload)
    const file = files[0];
    await handleFileUpload(file);

    // Reset input
    event.target.value = "";
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Only take the first file (single file upload)
    const file = files[0];
    await handleFileUpload(file);

    if (files.length > 1) {
      toast.info("Only one file can be uploaded. The first file was selected.");
    }
  };

  const handleRemoveDocument = () => {
    if (uploadedDocument?.previewUrl) {
      URL.revokeObjectURL(uploadedDocument.previewUrl);
    }
    setUploadedDocument(null);
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  // ============ FILE PREVIEW HANDLERS ============
  const generateSignedUrl = async (doc_name: string) => {
    try {
      const signedUrl = await getSignedPreviewUrl({
        document_name: doc_name,
        dispatch,
        getSignedUrlThunk: isROW
          ? getFileSignedURLRowThunk
          : getFileSignedURLAccessThunk,
      });

      if (!signedUrl) return;

      const response = await fetch(signedUrl, { credentials: "include" });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewDocUrl(blobUrl);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePreview = async () => {
    if (!uploadedDocument || uploadedDocument.isUploading) return;

    setPreviewOpen(true);

    // If it's a local file (just uploaded), use the blob URL directly
    if (uploadedDocument.previewUrl?.startsWith("blob:")) {
      setPreviewDocUrl(uploadedDocument.previewUrl);
      return;
    }

    // Otherwise fetch signed URL
    const objectKey = getObjectKey(uploadedDocument?.objectKey);
    if (!objectKey) return;

    await generateSignedUrl(objectKey);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  // ============ FILE PREVIEW RENDERER (SINGLE) ============
  const renderFilePreview = () => {
    if (!uploadedDocument) return null;

    const file = uploadedDocument;

    if (file.fileType === "image" || file.fileType === "video") {
      return (
        <Card
          sx={{
            maxWidth: { xs: "100px", sm: "120px" },
            m: 0.5,
            position: "relative",
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
              }}
            />
          )}

          <Box display="flex" position="relative">
            {file.fileType === "image" ? (
              <CardMedia
                component="img"
                image={file.previewUrl}
                alt={file.fileName}
                sx={{
                  objectFit: "cover",
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                  opacity: file.isUploading ? 0.7 : 1,
                  width: "100%",
                  height: { xs: "80px", sm: "90px" },
                }}
                onClick={() => !file.isUploading && handlePreview()}
              />
            ) : (
              <CardMedia
                component="video"
                src={file.previewUrl}
                sx={{
                  objectFit: "cover",
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                  opacity: file.isUploading ? 0.7 : 1,
                  width: "100%",
                  height: { xs: "80px", sm: "90px" },
                }}
              />
            )}

            {file.fileType === "video" && (
              <Box
                onClick={() => !file.isUploading && handlePreview()}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  p: 0.5,
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                }}
              >
                <PlayCircleOutlineIcon fontSize="large" />
              </Box>
            )}
          </Box>

          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Typography
              variant="caption"
              noWrap
              sx={{ fontSize: { xs: 10, sm: 12 } }}
            >
              {file.fileName}
              {file.isUploading && ` (${file.uploadProgress}%)`}
            </Typography>
          </CardContent>
        </Card>
      );
    } else if (file.fileType === "pdf") {
      return (
        <Card
          sx={{
            maxWidth: { xs: "120px", sm: "160px" },
            m: 0.5,
            position: "relative",
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
            />
          )}
          <CardContent
            sx={{ p: 1, cursor: file.isUploading ? "not-allowed" : "pointer" }}
            onClick={() => !file.isUploading && handlePreview()}
          >
            <PictureAsPdfIcon
              sx={{ fontSize: { xs: 22, sm: 28 }, color: "#E53935" }}
            />
            <Typography
              variant="caption"
              display="block"
              noWrap
              sx={{ fontSize: { xs: 10, sm: 12 } }}
            >
              {file.fileName}
              {file.isUploading && ` (${file.uploadProgress}%)`}
            </Typography>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 1,
            p: 1,
            m: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            fontSize: { xs: 12, sm: 14 },
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
            />
          )}
          <AttachFileOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          <Typography
            variant="caption"
            noWrap
            sx={{
              maxWidth: { xs: "70px", sm: "100px" },
              fontSize: { xs: 10, sm: 12 },
            }}
          >
            {file.fileName}
            {file.isUploading && ` (${file.uploadProgress}%)`}
          </Typography>
        </Box>
      );
    }
  };

  // ============ SUBMIT HANDLER ============
  const handleSubmit = async () => {
    // 1. Validate reason
    if (!reason.trim()) {
      setError(true);
      return;
    }
    setError(false);

    // 2. Check if file is still uploading
    if (uploadedDocument?.isUploading) {
      toast.error("Please wait for the file to finish uploading");
      return;
    }

    try {
      setIsSubmitting(true);

      // 3. Prepare the payload with single document
      const payload = {
        application_id: applicationId,
        appeal_remark: reason.trim(),
        appeal_documents: uploadedDocument
          ? {
              fileName: uploadedDocument.fileName,
              objectKey: uploadedDocument.objectKey,
              originalFileName:
                uploadedDocument.originalFileName || uploadedDocument.fileName,
            }
          : null,
      };

      console.log("Submitting payload:", payload);

      // 4. Dispatch the thunk
      const response = await dispatch(
        submitReviewRequestThunk(payload),
      ).unwrap();

      // 5. Handle success
      if (response?.success !== false) {
        // Cleanup blob URL
        if (uploadedDocument?.previewUrl) {
          URL.revokeObjectURL(uploadedDocument.previewUrl);
        }

        // Reset form state
        setReason("");
        setUploadedDocument(null);

        // Notify parent
        onSuccess?.();

        // Show success modal
        setSuccessModalOpen(true);
      } else {
        toast.error(response?.message || "Submission failed");
      }
    } catch (error: any) {
      console.error("Failed to submit review request:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to submit review request",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    !reason.trim() || isSubmitting || !!uploadedDocument?.isUploading;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 600 }}>
            Send Request for Review
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body Content */}
        <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
          {/* Reason Input */}
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 600, color: "#333" }}
          >
            Review Request Reason <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter Reason"
            variant="outlined"
            value={reason}
            onChange={(e) => {
              if (e.target.value.length <= 500) {
                setReason(e.target.value);
                if (error) setError(false);
              }
            }}
            error={error}
            helperText={error ? "Reason is required" : `${reason.length}/500`}
            disabled={isSubmitting}
            sx={{ mb: 3 }}
          />

          {/* Document Upload Section */}
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 600, color: "#333" }}
          >
            Supporting Document (Single file only)
          </Typography>

          {/* Upload Area - hidden when file already exists */}
          {!uploadedDocument && (
            <Box
              onClick={isSubmitting ? undefined : handleBoxClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                border: `1.5px dashed ${isDragging ? "#2B0A57" : "#C7C7C7"}`,
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                textAlign: "center",
                bgcolor: isDragging ? "#F0F0F0" : "#FAFAFA",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                opacity: isSubmitting ? 0.7 : 1,
                "&:hover": {
                  borderColor: isSubmitting ? "#C7C7C7" : "#2B0A57",
                  bgcolor: isSubmitting ? "#FAFAFA" : "#F5F5F5",
                },
              }}
            >
              <CloudUploadOutlinedIcon
                sx={{
                  color: "#2B0A57",
                  mb: 1,
                  width: { xs: 24, sm: 30 },
                  height: { xs: 18, sm: 22 },
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: { xs: "12px", sm: "14px" },
                  lineHeight: "20px",
                }}
              >
                Drag & Drop File or{" "}
                <span
                  style={{
                    color: "#2B0A57",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Browse File
                </span>
                <br />
                <span style={{ fontSize: "12px", color: "#757575" }}>
                  Please upload up to 20 MB. Supports PDF, images, and videos.
                </span>
              </Typography>

              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="application/pdf, image/*, video/*"
                disabled={isSubmitting}
              />
            </Box>
          )}

          {/* Upload Status Alert */}
          {uploadedDocument?.isUploading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Uploading file... Please wait.
            </Alert>
          )}

          {/* Uploaded File Preview */}
          {uploadedDocument && (
            <Box mt={2} sx={{ width: "100%", boxSizing: "border-box" }}>
              <Typography
                variant="body2"
                color="textSecondary"
                mb={1}
                sx={{ fontSize: { xs: 12, sm: 14 } }}
              >
                Uploaded file {!uploadedDocument.isUploading && "(ready)"}:
              </Typography>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                {renderFilePreview()}
                {!uploadedDocument.isUploading && !isSubmitting && (
                  <IconButton
                    size="small"
                    onClick={handleRemoveDocument}
                    sx={{
                      position: "absolute",
                      zIndex: 5,
                      top: 4,
                      right: 4,
                      bgcolor: "#00000042",
                      color: "white",
                      width: { xs: 22, sm: 25 },
                      borderRadius: "unset",
                      height: { xs: 20, sm: 22 },
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer / Submit Button */}
        <Box sx={{ p: 2, borderTop: "1px solid #E0E0E0" }}>
          <Button
            fullWidth
            variant="contained"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              backgroundColor: !isSubmitDisabled ? "#2B0A57" : "#E0E0E0",
              color: !isSubmitDisabled ? "#FFF" : "#9E9E9E",
              textTransform: "none",
              borderRadius: "24px",
              py: 1.2,
              "&:hover": {
                backgroundColor: !isSubmitDisabled ? "#1A0636" : "#E0E0E0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#F5F5F5",
                color: "#BDBDBD",
              },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </Box>
      </Drawer>

      {/* File Preview Modal */}
      {uploadedDocument && (
        <FilePreviewModal
          open={previewOpen}
          onClose={handleClosePreview}
          file={uploadedDocument}
          fileType={uploadedDocument.fileType}
          previewDocUrl={previewDocUrl}
        />
      )}

      <Dialog
        open={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          onClose();
        }}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            maxWidth: "400px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            pb: 1,
          }}
        >
          Request for Review
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderBottom: "none",
            borderTop: "1px solid #E0E0E0",
            pt: 3,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: "#43B864",
              }}
            />
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: "20px",
              fontWeight: 700,
              color: "#222",
              mb: 1,
            }}
          >
            Request Submitted Successfully
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: "14px",
              color: "#666",
              lineHeight: 1.6,
            }}
          >
            Your request for appeal review has been submitted successfully.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setSuccessModalOpen(false);
              onClose();
            }}
            sx={{
              borderRadius: "24px",
              backgroundColor: "#3A1C73",
              textTransform: "none",
              fontWeight: 500,
              py: 1.2,
              "&:hover": {
                backgroundColor: "#2A145C",
              },
            }}
          >
            OK, Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
